import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getAdminAliases, setImpersonationContext } from '../services/apiClient';

export interface Alias {
  user_id: string;
  company_id: string;
  company_name: string;
}

export interface ImpersonationContextValue {
  activeAlias: Alias | null;
  aliases: Alias[];
  isLoading: boolean;
  error: string | null;
  isImpersonating: boolean;
  canImpersonate: boolean;
  fetchAliases: () => Promise<void>;
  setActiveAlias: (alias: Alias | null) => void;
  clearImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

const STORAGE_KEY = 'corpus_impersonation_alias';

export const ImpersonationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authStatus, isLoading: isAuthLoading } = useAuth();
  const user = authStatus?.user;

  const [activeAlias, setActiveAliasState] = useState<Alias | null>(null);
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  // Check if user can impersonate (CorpusJuris admin only, case-insensitive)
  const canImpersonate = user?.is_admin === true && user?.company?.toLowerCase() === 'corpusjuris';

  const isImpersonating = activeAlias !== null;

  // Reset initialization when user identity changes (e.g., different user logs in)
  useEffect(() => {
    const currentUserId = user?.id;
    if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== currentUserId) {
      // User changed - reset initialization to re-evaluate impersonation state
      setIsInitialized(false);
      setActiveAliasState(null);
      setAliases([]);
      sessionStorage.removeItem(STORAGE_KEY);
      setImpersonationContext(null);
    }
    prevUserIdRef.current = currentUserId;
  }, [user?.id]);

  // Load saved alias from sessionStorage on mount - only when auth is ready
  useEffect(() => {
    // Wait for auth to be determined (not loading)
    if (isAuthLoading) {
      return;
    }

    // Only run initialization once
    if (isInitialized) {
      return;
    }

    if (!canImpersonate) {
      // Clear any stored impersonation if user can't impersonate
      sessionStorage.removeItem(STORAGE_KEY);
      setActiveAliasState(null);
      setImpersonationContext(null);
      setIsInitialized(true);
      return;
    }

    const stored = sessionStorage.getItem(STORAGE_KEY);

    if (stored) {
      try {
        const alias = JSON.parse(stored) as Alias;
        setActiveAliasState(alias);
        setImpersonationContext({
          userId: alias.user_id,
          companyId: alias.company_id
        });
      } catch (e) {
        // Failed to parse stored alias - clear it
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsInitialized(true);
  }, [isAuthLoading, canImpersonate, isInitialized]);

  // Fetch available aliases from API
  const fetchAliases = useCallback(async () => {
    if (!canImpersonate) {
      setAliases([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getAdminAliases();
      if (response.success && response.data) {
        // Use 'items' from the new API response schema
        setAliases(response.data.items || []);
      } else {
        setError(response.error || 'Erreur lors du chargement des entreprises');
        setAliases([]);
      }
    } catch (err) {
      setError('Erreur réseau');
      setAliases([]);
    } finally {
      setIsLoading(false);
    }
  }, [canImpersonate]);

  // Set active alias
  const setActiveAlias = useCallback((alias: Alias | null) => {
    setActiveAliasState(alias);

    if (alias) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(alias));
      setImpersonationContext({
        userId: alias.user_id,
        companyId: alias.company_id
      });
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
      setImpersonationContext(null);
    }
  }, []);

  // Clear impersonation
  const clearImpersonation = useCallback(() => {
    setActiveAliasState(null);
    sessionStorage.removeItem(STORAGE_KEY);
    setImpersonationContext(null);
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{
        activeAlias,
        aliases,
        isLoading,
        error,
        isImpersonating,
        canImpersonate,
        fetchAliases,
        setActiveAlias,
        clearImpersonation
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextValue => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
